import { Checkbox, InputNumber } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Modal from 'antd/lib/modal/Modal';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCircleFilled } from '@ant-design/icons';
import {
    BACKSPLASH,
    BACKSPLASH_DEFAULT_VALUE,
    BACKSPLASH_INFO_IMAGE,
    BACKSPLASH_INPUT_LIMIT,
    UNIT_MEASURE,
    WATERFALL_INPUT_LIMIT,
} from '@common/constants';
import { SideItemType } from '@common/enums';
import { roundToArrowLengthGranularity } from '@common/helpers';
import formatUOM from '@common/helpers/formatUOM';
import { DrawingShape, SideItemInfoModel } from '@common/models';
import { adjustValueForMinMax, handleDecimalNumberInput } from '@common/utils';
import { useSideItemBoundsNotification } from '@core/hooks/useSideItemBoundsNotification';
import { useShapeContext } from '@ee-context';

import useSideItems from '../../../../fabCanvas/useSideItems';

import './SideItemRadio.scss';

/**
 * @param sideItem The item being displayed
 * @param shape The shape this item belongs to
 * @param label An alternate label to display for the checkox (used by laminate wall side)
 * @param isVisible is the item visible
 */
export interface SideItemRadioProps {
    sideItem: SideItemInfoModel;
    shape: DrawingShape;
    label?: string;
    isVisible?: boolean;
}

export interface ExtraItemProps {
    value: number;
}

/**
 * this is a clone with minor changes from the old checkboxCustom component
 * @param sideItem
 * @param _shape
 * @param label
 * @param isVisible
 * @constructor
 */
const SideItemRadio = ({ sideItem, shape: _shape, label, isVisible = true }: SideItemRadioProps): JSX.Element => {
    const { t } = useTranslation();
    const { updateSelectedSideItem } = useShapeContext();
    const { removeElementsBySideItem } = useSideItems();
    const { notifyIfOutOfBounds } = useSideItemBoundsNotification();

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const ExtraInputItem = ({ value }: ExtraItemProps): JSX.Element => {
        const [inputValue, setInputValue] = useState<number>(value);

        useEffect(() => {
            //Ensure the waterfall height exists to avoid type errors
            if (sideItem.waterfallHeight) {
                //Gotta make sure we correct it
                sideItem.waterfallHeight = adjustValueForMinMax(
                    roundToArrowLengthGranularity(sideItem.waterfallHeight),
                    WATERFALL_INPUT_LIMIT.MIN,
                    WATERFALL_INPUT_LIMIT.MAX
                );
            }
            // Handle backsplash height
            if (sideItem.backsplash?.height) {
                sideItem.backsplash.height = adjustValueForMinMax(
                    roundToArrowLengthGranularity(sideItem.backsplash.height),
                    BACKSPLASH_INPUT_LIMIT.MIN,
                    BACKSPLASH_INPUT_LIMIT.MAX
                );
            }
        });

        const onValueChange = (value: number | null) => {
            if (value) {
                setInputValue(value);
            }
        };

        const onInputBlur = (e: BaseSyntheticEvent) => {
            e.preventDefault();
            switch (sideItem.sideType) {
                case SideItemType.WATERFALL:
                    notifyIfOutOfBounds(true, WATERFALL_INPUT_LIMIT, 'WATERFALL', inputValue);
                    sideItem.lastEnteredLength = inputValue;
                    sideItem.waterfallHeight = adjustValueForMinMax(
                        roundToArrowLengthGranularity(inputValue),
                        WATERFALL_INPUT_LIMIT.MIN,
                        WATERFALL_INPUT_LIMIT.MAX
                    );

                    break;
                case SideItemType.WALL_SIDE:
                    notifyIfOutOfBounds(true, BACKSPLASH_INPUT_LIMIT, 'BACKSPLASH', inputValue);
                    sideItem.lastEnteredLength = inputValue;
                    sideItem.backsplash = {
                        checked: true,
                        height: adjustValueForMinMax(
                            roundToArrowLengthGranularity(inputValue),
                            BACKSPLASH_INPUT_LIMIT.MIN,
                            BACKSPLASH_INPUT_LIMIT.MAX
                        ),
                    };
                    break;
            }

            updateSelectedSideItem(sideItem);
        };

        const onInfoItemClicked = (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();

            setIsModalVisible(true);
        };

        return (
            <>
                <div className="extra-area">
                    <div
                        className={`description ${
                            sideItem.sideType === SideItemType.WALL_SIDE ? 'extra-descr-info' : 'extra-descr-no-info'
                        }`}
                    >
                        {t('SCROLLABLE_AREA.COUNTERTOP.HEIGHT')}&nbsp;({formatUOM(UNIT_MEASURE.SIDE_WALLS)})
                    </div>

                    <div className={`${sideItem.sideType === SideItemType.WALL_SIDE ? 'input-container' : ''}`}>
                        <InputNumber
                            className={`${
                                sideItem.sideType === SideItemType.WALL_SIDE ? 'backsplash-input' : 'waterfall-input'
                            }`}
                            value={inputValue}
                            onKeyDown={handleDecimalNumberInput}
                            onChange={onValueChange}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={onInputBlur}
                            onPressEnter={onInputBlur}
                        />
                        {sideItem.sideType === SideItemType.WALL_SIDE && (
                            <InfoCircleFilled
                                className="info-icon"
                                style={{ marginLeft: 10 }}
                                onClick={onInfoItemClicked}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    };

    const handleBacksplashCheckBoxChangeEvent = (event: CheckboxChangeEvent) => {
        sideItem.backsplash = {
            checked: event.target.checked,
            height: BACKSPLASH_DEFAULT_VALUE,
        };

        updateSelectedSideItem(sideItem);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        sideItem.checked = event.target.checked;
        updateSelectedSideItem(sideItem);

        if (event.target.checked) {
            removeElementsBySideItem(sideItem);
        }
    };

    if (!isVisible) {
        return <></>;
    }

    return (
        <div className={`checkbox-container ${sideItem.disabled ? 'disabled-side-item' : ''}`}>
            <input
                type="checkbox"
                className="checkbox-round"
                onChange={handleChange}
                value={sideItem.value}
                name={`customrb${sideItem.id}-${sideItem.label}`}
                checked={sideItem.checked}
                disabled={sideItem.disabled}
            />
            <label>{label ?? sideItem.value}</label>

            {sideItem.sideType === SideItemType.WALL_SIDE && sideItem.checked && (
                <div className={`extra-check-box ${sideItem?.backsplash?.checked ? 'checkedBacksplash' : ''}`}>
                    <Checkbox
                        checked={sideItem?.backsplash?.checked}
                        onChange={handleBacksplashCheckBoxChangeEvent}
                        className="backsplash-style"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {BACKSPLASH}
                    </Checkbox>
                    {sideItem.backsplash && sideItem.backsplash.checked && sideItem.backsplash.height && (
                        <ExtraInputItem value={sideItem.backsplash.height} />
                    )}
                </div>
            )}
            {sideItem.sideType === SideItemType.WATERFALL && sideItem.checked && sideItem.waterfallHeight && (
                <div className="waterfall-height">
                    <ExtraInputItem value={sideItem.waterfallHeight} />
                </div>
            )}
            <Modal
                className="preview-modal"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                }}
                closable
                destroyOnClose
                centered
                footer={null}
            >
                <div className="description-modal">
                    {t('SHAPE_TAB.BACKSPLASH_INFO')}
                    <img className="img-info" src={BACKSPLASH_INFO_IMAGE} />
                </div>
            </Modal>
        </div>
    );
};

export default SideItemRadio;
